import React, { useState } from "react";
import SortableList, { SortableItem } from "react-easy-sort";
import arrayMove from "array-move";
import { Box, Typography, IconButton, TextField } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import imagenLocal from "../../../../assets/images/sections.jpg";
import {
  CustomDialog,
  CustomDialogTitle,
  CustomDialogContent,
  CustomDialogActions,
} from "../../../../components/customDialog";
import { CloseButton, SendButton } from "../../../../components/buttons";
import BackgroundImageSelectionDialog from "../image_components/background_image_selection_dialog";

function SectionsMenuBuild({
  items,
  onClickProducts,
  setHasChanges,
  setItemsWithCorrectOrderValue,
  onDeleteSection,
  onEditSection, // nuevo callback: (section, newName, newImageUrl, newImageId) => void
}) {
  const [currentItems, setCurrentItems] = useState([]);

  // ── Estado para eliminar ─────────────────────────────────────
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);

  // ── Estado para editar ───────────────────────────────────────
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [sectionToEdit, setSectionToEdit] = useState(null);
  const [editName, setEditName] = useState("");
  const [editImageUrl, setEditImageUrl] = useState("");
  const [editImageId, setEditImageId] = useState(null);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  React.useEffect(() => {
    const sortedItems = [...items].sort((a, b) => a.order - b.order);
    setCurrentItems(sortedItems);
  }, [items]);

  // ── Handlers de Orden ────────────────────────────────────────
  const onSortEnd = (oldIndex, newIndex) => {
    const newItems = arrayMove(currentItems, oldIndex, newIndex).map((item, index) => ({
      ...item,
      order: index + 1,
    }));
    setCurrentItems(newItems);
    setItemsWithCorrectOrderValue(newItems);
    setHasChanges(true);
  };

  // ── Handlers de Eliminar ─────────────────────────────────────
  const handleDeleteClick = (e, item) => {
    e.stopPropagation();
    const hasProducts = item.products && item.products.length > 0;
    if (hasProducts) {
      setSectionToDelete(item);
      setConfirmDeleteDialogOpen(true);
    } else {
      onDeleteSection(item.order);
    }
  };

  const handleConfirmDelete = () => {
    if (sectionToDelete) {
      onDeleteSection(sectionToDelete.order);
    }
    setConfirmDeleteDialogOpen(false);
    setSectionToDelete(null);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteDialogOpen(false);
    setSectionToDelete(null);
  };

  // ── Handlers de Editar ───────────────────────────────────────
  const handleEditClick = (e, item) => {
    e.stopPropagation();
    setSectionToEdit(item);
    setEditName(item.name || "");
    setEditImageUrl(item.background_image_url || "");
    setEditImageId(item.background_image_id || null);
    setEditDialogOpen(true);
  };

  const handleImageSelect = (image) => {
    setEditImageUrl(image.image_url || "");
    setEditImageId(image.image_id || image.id || null);
    setImagePickerOpen(false);
  };

  const handleConfirmEdit = () => {
    if (!editName.trim()) return;
    if (onEditSection) {
      onEditSection(sectionToEdit, editName.trim(), editImageUrl, editImageId);
    }
    setEditDialogOpen(false);
    setSectionToEdit(null);
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setSectionToEdit(null);
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
        <SortableList onSortEnd={onSortEnd} className="list" draggedItemClassName="dragged">
          {currentItems.map((item) => (
            <SortableItem key={item.order}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px",
                  borderRadius: "8px",
                  marginBottom: "10px",
                  width: "100%",
                  cursor: "pointer",
                  backgroundImage: `url(${item.background_image_url || imagenLocal})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  color: "white",
                  textShadow: "1px 1px 4px rgba(0,0,0,0.7)",
                  minHeight: "100px",
                  position: "relative",
                }}
                onClick={() => onClickProducts(item)}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {item.order}. {item.name}
                </Typography>

                {/* Botones de acción: Editar + Eliminar */}
                <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 0.5, zIndex: 10 }}>
                  {/* Botón Editar */}
                  <IconButton
                    size="small"
                    onClick={(e) => handleEditClick(e, item)}
                    sx={{
                      backgroundColor: "rgba(0,0,0,0.5)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(59,130,246,0.75)",
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>

                  {/* Botón Eliminar */}
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteClick(e, item)}
                    sx={{
                      backgroundColor: "rgba(0,0,0,0.5)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255,0,0,0.7)",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </SortableItem>
          ))}
        </SortableList>
      </Box>

      {/* ── Diálogo de Edición de Sección ─────────────────────── */}
      <CustomDialog open={editDialogOpen} onClose={handleCancelEdit}>
        <CustomDialogTitle>Editar Sección</CustomDialogTitle>
        <CustomDialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {/* Campo nombre */}
            <TextField
              autoFocus
              required
              label="Nombre de la Sección"
              fullWidth
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "12px" },
              }}
            />

            {/* Preview de imagen actual / nueva */}
            {editImageUrl && (
              <Box
                sx={{
                  width: "100%",
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: "1px dashed #e0e0e0",
                  textAlign: "center",
                  p: 1,
                  bgcolor: "#f9f9f9",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Imagen actual
                </Typography>
                <Box
                  component="img"
                  src={editImageUrl}
                  alt="preview"
                  sx={{ maxWidth: "100%", maxHeight: "140px", borderRadius: "8px", objectFit: "cover" }}
                />
              </Box>
            )}

            {/* Botón para cambiar imagen */}
            <SendButton
              onClick={() => setImagePickerOpen(true)}
              sx={{ width: "100%", py: 1.2 }}
            >
              {editImageUrl ? "Cambiar imagen de fondo" : "Seleccionar imagen de fondo"}
            </SendButton>
          </Box>
        </CustomDialogContent>
        <CustomDialogActions>
          <CloseButton onClick={handleCancelEdit}>Cancelar</CloseButton>
          <SendButton onClick={handleConfirmEdit} disabled={!editName.trim()}>
            Guardar
          </SendButton>
        </CustomDialogActions>
      </CustomDialog>

      {/* Selector de imagen para editar */}
      <BackgroundImageSelectionDialog
        open={imagePickerOpen}
        handleClose={() => setImagePickerOpen(false)}
        restaurantId={sectionToEdit?.restaurant_id}
        onSelectImage={handleImageSelect}
      />

      {/* ── Diálogo de confirmación de eliminación ─────────────── */}
      <CustomDialog
        open={confirmDeleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="confirm-delete-dialog-title"
      >
        <CustomDialogTitle id="confirm-delete-dialog-title">
          ¿Eliminar sección?
        </CustomDialogTitle>
        <CustomDialogContent>
          <Typography variant="body1">
            Esta sección tiene productos asociados. Si la eliminas, también se eliminarán todos sus
            productos. ¿Estás seguro de que deseas continuar?
          </Typography>
        </CustomDialogContent>
        <CustomDialogActions>
          <CloseButton onClick={handleCancelDelete}>Cancelar</CloseButton>
          <SendButton onClick={handleConfirmDelete} autoFocus>
            Eliminar
          </SendButton>
        </CustomDialogActions>
      </CustomDialog>
    </>
  );
}

export default SectionsMenuBuild;